import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

/* ================= PAGES ================= */
import Home from "./Component/HomeSectionm/Home";
import Blog from "./Component/Common/Blog";
import Help from "./Component/Common/Help";
import SignIn from "./Component/Common/SignIn";
import Signup from "./Component/Common/Signup";
import ForgotPassword from "./Component/Common/ForgotPassword";
import ResetPassword from "./Component/Common/ResetPassword";
import VerifyEmail from "./Component/Common/VerifyEmail";

import UserDashboard from "./UserDashboard/UserDashboard";
import Domain from "./Component/ServerPackage/Domain";
import Email from "./Component/ServerPackage/Email";
import Marketing from "./Component/ServerPackage/Marketing";
import Security from "./Component/ServerPackage/Security";
import Hosting from "./Component/ServerPackage/Hosting/Hosting";

import ProfilePage from "./Component/ProfileUser/ProfileSettings";
import SettingsPage from "./Component/ProfileUser/SettingsPage";
import CartPage from "./Component/Common/CartPage";
import PaymentHistory from "./Component/PaymentHistory/PaymentHistory";

import OrdersPage from "./UserDashboard/OrdersPage";
import FavoritesPage from "./UserDashboard/FavoritesPage";
import NotificationsPage from "./UserDashboard/NotificationsPage";
import ActivityPage from "./UserDashboard/ActivityPage";
import PlansPage from "./UserDashboard/PlansPage";
import WalletPage from "./UserDashboard/WalletPage";

import DNSManage from "./Component/ServerPackage/Hosting/DNSManage";
import AIStore from "./Component/ServerPackage/Hosting/AIStore";
import MaintenanceDoor from "./Component/ServerPackage/Hosting/MaintenanceDoor";
import AdminHostingSetup from "./Component/ServerPackage/Hosting/AdminHostingSetup";
import UserHostingSetup from "./Component/ServerPackage/Hosting/UserHostingSetup";
import SimpleCpanel from "./Component/HostingCpanel/SimpleCpanel";

/* ================= AUTH HELPERS ================= */
const AUTH_TOKEN_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
];

const AUTH_USER_KEYS = [
  "isLoggedIn",
  "user",
  "currentUser",
  "email",
  "userEmail",
  "user_email",
  "fullName",
  "name",
  "userName",
  "username",
];

const getStorageValue = (key) => {
  if (typeof window === "undefined") return "";

  return localStorage.getItem(key) || sessionStorage.getItem(key) || "";
};

const getToken = () => {
  if (typeof window === "undefined") return "";

  for (const key of AUTH_TOKEN_KEYS) {
    const value = getStorageValue(key);

    if (value && String(value).trim()) {
      return String(value).trim();
    }
  }

  return "";
};

const decodeJwtPayload = (token) => {
  try {
    if (!token || !token.includes(".")) return null;

    const base64Url = token.split(".")[1];

    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");

    const paddedBase64 = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "="
    );

    const jsonPayload = decodeURIComponent(
      atob(paddedBase64)
        .split("")
        .map((char) => {
          return `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`;
        })
        .join("")
    );

    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

const isTokenValid = (token) => {
  const payload = decodeJwtPayload(token);

  if (!payload) return false;

  if (!payload.exp) {
    return true;
  }

  return payload.exp * 1000 > Date.now();
};

const clearAuthStorage = () => {
  if (typeof window === "undefined") return;

  [...AUTH_TOKEN_KEYS, ...AUTH_USER_KEYS].forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
};

const hasValidAuth = () => {
  const token = getToken();

  if (!token) return false;

  return isTokenValid(token);
};

/* ================= AUTH HOOK ================= */
const useAuth = () => {
  const [isAuth, setIsAuth] = useState(null);

  useEffect(() => {
    const checkAuth = () => {
      if (typeof window === "undefined") {
        setIsAuth(false);
        return;
      }

      const valid = hasValidAuth();

      if (!valid) {
        clearAuthStorage();
        setIsAuth(false);
        return;
      }

      localStorage.setItem("isLoggedIn", "true");
      setIsAuth(true);
    };

    checkAuth();

    const sync = () => {
      checkAuth();
    };

    window.addEventListener("storage", sync);
    window.addEventListener("auth-state-changed", sync);
    window.addEventListener("login-success", sync);

    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("auth-state-changed", sync);
      window.removeEventListener("login-success", sync);
    };
  }, []);

  return isAuth;
};

/* ================= ROUTE GUARDS ================= */
const LoadingScreen = () => {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "#ffffff",
        color: "#0f172a",
        fontFamily: "Arial, sans-serif",
      }}
    >
      Loading...
    </div>
  );
};

const PublicOnlyRoute = ({ children, isAuth }) => {
  if (isAuth === null) return <LoadingScreen />;

  return isAuth ? <Navigate to="/dashboard" replace /> : children;
};

const PrivateRoute = ({ children, isAuth }) => {
  const location = useLocation();

  if (isAuth === null) return <LoadingScreen />;

  return isAuth ? (
    children
  ) : (
    <Navigate to="/signin" replace state={{ from: location.pathname }} />
  );
};

PublicOnlyRoute.propTypes = {
  children: PropTypes.node.isRequired,
  isAuth: PropTypes.bool,
};

PrivateRoute.propTypes = {
  children: PropTypes.node.isRequired,
  isAuth: PropTypes.bool,
};

/* ================= APP ================= */
function App() {
  const isAuth = useAuth();

  if (isAuth === null) {
    return <LoadingScreen />;
  }

  return (
    <Routes>
      {/* ================= PUBLIC ROUTES ================= */}
      <Route
        path="/"
        element={isAuth ? <Navigate to="/dashboard" replace /> : <Home />}
      />

      <Route path="/blog" element={<Blog />} />
      <Route path="/help" element={<Help />} />

      <Route
        path="/signin"
        element={
          <PublicOnlyRoute isAuth={isAuth}>
            <SignIn />
          </PublicOnlyRoute>
        }
      />

      <Route
        path="/signup"
        element={
          <PublicOnlyRoute isAuth={isAuth}>
            <Signup />
          </PublicOnlyRoute>
        }
      />

      <Route
        path="/forgot-password"
        element={
          <PublicOnlyRoute isAuth={isAuth}>
            <ForgotPassword />
          </PublicOnlyRoute>
        }
      />

      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/reset/:token" element={<ResetPassword />} />

      <Route path="/verify-email/:token" element={<VerifyEmail />} />
      <Route path="/verify/:token" element={<VerifyEmail />} />

      {/* ================= PRIVATE MAIN ROUTES ================= */}
      <Route
        path="/dashboard"
        element={
          <PrivateRoute isAuth={isAuth}>
            <UserDashboard />
          </PrivateRoute>
        }
      />

      <Route
        path="/domains"
        element={
          <PrivateRoute isAuth={isAuth}>
            <Domain />
          </PrivateRoute>
        }
      />

      <Route
        path="/hosting"
        element={
          <PrivateRoute isAuth={isAuth}>
            <Hosting />
          </PrivateRoute>
        }
      />

      <Route
        path="/hosting/setup"
        element={
          <PrivateRoute isAuth={isAuth}>
            <UserHostingSetup />
          </PrivateRoute>
        }
      />

      {/* ================= CUSTOM HOSTING PANEL ROUTES ================= */}
      <Route
        path="/hosting/panel"
        element={
          <PrivateRoute isAuth={isAuth}>
            <SimpleCpanel />
          </PrivateRoute>
        }
      />

      <Route
        path="/cpanel"
        element={
          <PrivateRoute isAuth={isAuth}>
            <SimpleCpanel />
          </PrivateRoute>
        }
      />

      <Route
        path="/hosting/cpanel"
        element={
          <PrivateRoute isAuth={isAuth}>
            <SimpleCpanel />
          </PrivateRoute>
        }
      />

      <Route
        path="/simple-cpanel"
        element={
          <PrivateRoute isAuth={isAuth}>
            <SimpleCpanel />
          </PrivateRoute>
        }
      />

      <Route
        path="/email"
        element={
          <PrivateRoute isAuth={isAuth}>
            <Email />
          </PrivateRoute>
        }
      />

      <Route
        path="/marketing"
        element={
          <PrivateRoute isAuth={isAuth}>
            <Marketing />
          </PrivateRoute>
        }
      />

      <Route
        path="/security"
        element={
          <PrivateRoute isAuth={isAuth}>
            <Security />
          </PrivateRoute>
        }
      />

      {/* ================= SERVICE ROUTES ================= */}
      <Route
        path="/dns-manage"
        element={
          <PrivateRoute isAuth={isAuth}>
            <DNSManage />
          </PrivateRoute>
        }
      />

      <Route
        path="/ai-store"
        element={
          <PrivateRoute isAuth={isAuth}>
            <AIStore />
          </PrivateRoute>
        }
      />

      <Route
        path="/maintaince-door"
        element={
          <PrivateRoute isAuth={isAuth}>
            <MaintenanceDoor />
          </PrivateRoute>
        }
      />

      <Route
        path="/maintenance-door"
        element={
          <PrivateRoute isAuth={isAuth}>
            <MaintenanceDoor />
          </PrivateRoute>
        }
      />

      {/* ================= ADMIN ROUTES ================= */}
      <Route
        path="/admin/hosting-setup"
        element={
          <PrivateRoute isAuth={isAuth}>
            <AdminHostingSetup />
          </PrivateRoute>
        }
      />

      {/* ================= USER ROUTES ================= */}
      <Route
        path="/profile"
        element={
          <PrivateRoute isAuth={isAuth}>
            <ProfilePage />
          </PrivateRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <PrivateRoute isAuth={isAuth}>
            <SettingsPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/cart"
        element={
          <PrivateRoute isAuth={isAuth}>
            <CartPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/payment-history"
        element={
          <PrivateRoute isAuth={isAuth}>
            <PaymentHistory />
          </PrivateRoute>
        }
      />

      <Route
        path="/orders"
        element={
          <PrivateRoute isAuth={isAuth}>
            <OrdersPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/favorites"
        element={
          <PrivateRoute isAuth={isAuth}>
            <FavoritesPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/notifications"
        element={
          <PrivateRoute isAuth={isAuth}>
            <NotificationsPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/activity"
        element={
          <PrivateRoute isAuth={isAuth}>
            <ActivityPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/plans"
        element={
          <PrivateRoute isAuth={isAuth}>
            <PlansPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/wallet"
        element={
          <PrivateRoute isAuth={isAuth}>
            <WalletPage />
          </PrivateRoute>
        }
      />

      {/* ================= FALLBACK ================= */}
      <Route
        path="*"
        element={
          isAuth ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <Navigate to="/signin" replace />
          )
        }
      />
    </Routes>
  );
}

export default App;